import { useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminApi } from "@/lib/admin-api";
import { fmtDate, fmtRelative, useAdminList } from "./_useAdminList";

export default function AdminStores() {
  const [q, setQ] = useState("");
  const list = useAdminList({
    fetcher: adminApi.stores,
    dataKey: "stores",
    defaultPageSize: 50,
  });
  const [busyStoreId, setBusyStoreId] = useState(null);

  const triggerStoreSync = async (storeId) => {
    setBusyStoreId(storeId);
    const t = toast.loading(`Refreshing store ${storeId}…`);
    try {
      const data = await adminApi.ops.syncStore(storeId);
      toast.success(data?.message || "Refresh queued.", { id: t });
      list.refetch();
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.response?.data?.error || err.message || "Refresh failed",
        { id: t },
      );
    } finally {
      setBusyStoreId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(list.total / list.pageSize));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Stores</h1>
          <p className="text-sm text-muted-foreground">
            Every Shopify store known to RADAR. {list.total.toLocaleString()} total.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => list.refetch()}
          disabled={list.refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${list.refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") list.setFilters({ q });
            }}
            placeholder="Search shop domain or name…"
            className="pl-9 h-9"
          />
        </div>
        <Button size="sm" variant="secondary" onClick={() => list.setFilters({ q })}>
          Search
        </Button>
        {q && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setQ("");
              list.setFilters({ q: "" });
            }}
          >
            Clear
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {list.loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading stores…
            </div>
          ) : list.error ? (
            <div className="p-6 text-sm text-destructive">{list.error}</div>
          ) : list.data.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No stores found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead>Shop</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Sync</TableHead>
                  <TableHead className="text-right">Competitors</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead>Connected</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.id}</TableCell>
                    <TableCell>
                      <div className="font-medium">{s.shopDomain}</div>
                      {s.shopName && (
                        <div className="text-xs text-muted-foreground">{s.shopName}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {s.user?.email || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">{s.plan || "—"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          s.syncStatus === "complete"
                            ? "default"
                            : s.syncStatus === "syncing"
                              ? "secondary"
                              : s.syncStatus === "failed"
                                ? "destructive"
                                : "outline"
                        }
                        className="text-xs"
                      >
                        {s.syncStatus || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s._count?.competitors ?? 0}
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">{fmtRelative(s.lastSyncAt)}</div>
                      <div className="text-[10px] text-muted-foreground">{fmtDate(s.lastSyncAt)}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">{fmtRelative(s.connectedAt)}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyStoreId === s.id}
                        onClick={() => triggerStoreSync(s.id)}
                      >
                        {busyStoreId === s.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        <span className="ml-1.5">Sync</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>
          Page {list.page} of {totalPages} · {list.total.toLocaleString()} stores
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={list.page <= 1}
            onClick={() => list.setPage(list.page - 1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={list.page >= totalPages}
            onClick={() => list.setPage(list.page + 1)}
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
